/**
 * Renderer + post-processing + dynamic resolution — WebGPU edition.
 *
 * Uses three's WebGPURenderer (WebGPU where available — Safari 26+/iOS 26+, Chrome, Edge — and
 * an automatic WebGL2 backend elsewhere). Post-processing is a TSL RenderPipeline:
 * scene pass (MSAA on desktop) → bloom → chromatic hit-pulse → vignette → tone mapping/sRGB.
 *
 * IMPORTANT for stage code: WebGPURenderer does NOT run GLSL ShaderMaterial/RawShaderMaterial or
 * onBeforeCompile patches. Use built-in materials (MeshStandardMaterial etc. are converted
 * automatically) or TSL node materials from 'three/webgpu' + 'three/tsl'. See docs/WEBGPU.md.
 *
 * Stages configure the look via ctx.post.configure({...}); Game owns the render call.
 * `?renderer=webgl2` forces the WebGL2 backend (debug/QA).
 */
import { ACESFilmicToneMapping, AgXToneMapping, PCFShadowMap, PerspectiveCamera, SRGBColorSpace, Scene, type Camera } from 'three';
import { RenderPipeline, WebGPURenderer } from 'three/webgpu';
import { float, pass, screenUV, smoothstep, uniform, vec2, vec4 } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { capAutoTier, type QualityProfile } from './quality';

/** Use the WebGPU backend by default (false = WebGL2 backend of WebGPURenderer). */
export const WEBGPU_DEFAULT = true;

export interface PostFxSettings {
  bloom?: { intensity?: number; threshold?: number; smoothing?: number; radius?: number };
  vignette?: { offset?: number; darkness?: number };
  /** Base chromatic aberration offset (0 = off). Damage pulses add on top. */
  chromatic?: number;
  toneMapping?: 'aces' | 'agx';
  exposure?: number;
}

export interface PostFxApi {
  configure(s: PostFxSettings): void;
  /** Short chromatic/flash pulse (e.g. on hit). */
  pulse(strength?: number): void;
  readonly enabled: boolean;
}

const DEFAULTS: PostFxSettings = {
  bloom: { intensity: 0.9, threshold: 0.62, smoothing: 0.2, radius: 0.72 },
  vignette: { offset: 0.32, darkness: 0.55 },
  chromatic: 0,
  toneMapping: 'aces',
  exposure: 1,
};

type BloomNodeLike = ReturnType<typeof bloom>;

/**
 * Max rendered pixels per frame by tier. Big Retina screens (e.g. a 16" MacBook Pro at 2x is
 * ~7.7 M px) are capped so fill-rate stays sane; the effective pixel ratio is derived from this.
 */
const PIXEL_BUDGET: Record<string, number> = { low: 1.0e6, medium: 2.2e6, high: 3.7e6 };

export class RenderSystem implements PostFxApi {
  readonly renderer: WebGPURenderer;
  /** 'webgpu' or 'webgl2' once init() resolved. */
  backend: 'webgpu' | 'webgl2' | 'pending' = 'pending';
  private pipeline?: RenderPipeline;
  private scenePass?: ReturnType<typeof pass>;
  private bloomNode?: BloomNodeLike;
  private uVigOffset = uniform(0.32);
  private uVigDark = uniform(0.55);
  private uChroma = uniform(0);
  private settings: PostFxSettings = structuredClone(DEFAULTS);
  private pulseAmount = 0;
  private scene?: Scene;
  private camera?: Camera;
  private blankScene = new Scene();
  private blankCamera = new PerspectiveCamera();
  /** Current dynamic pixel ratio. */
  pixelRatio = 1;
  private frameTimes: number[] = [];
  private lastAdjust = 0;
  private ready = false;

  constructor(
    readonly canvas: HTMLCanvasElement,
    public quality: QualityProfile,
  ) {
    // TEMP (#141): WebGL2 backend by default until the WebGPU stage-transition buffer bug is fixed;
    // ?renderer=webgpu opts in. Flip WEBGPU_DEFAULT back to true once fixed.
    const rp = new URLSearchParams(location.search).get('renderer');
    const forceWebGL = rp === 'webgl2' || (rp !== 'webgpu' && !WEBGPU_DEFAULT);
    this.renderer = new WebGPURenderer({
      canvas,
      antialias: !quality.postfx && quality.antialias,
      powerPreference: 'high-performance',
      forceWebGL,
    });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.shadowMap.enabled = quality.shadows;
    this.renderer.shadowMap.type = PCFShadowMap;
    this.pixelRatio = this.maxPixelRatio();
    this.renderer.setPixelRatio(this.pixelRatio);
    this.applyToneMapping();
    this.resize();
  }

  /** Must be awaited before the first render (WebGPU device acquisition). */
  async init(): Promise<void> {
    await this.renderer.init();
    const b = this.renderer.backend as unknown as { isWebGPUBackend?: boolean };
    this.backend = b.isWebGPUBackend ? 'webgpu' : 'webgl2';
    if (this.quality.postfx) this.buildPipeline();
    this.ready = true;
    console.info(`[render] backend=${this.backend} tier=${this.quality.tier} device=${this.quality.device}`);
  }

  get enabled(): boolean {
    return !!this.pipeline;
  }

  private buildPipeline(): void {
    // MSAA only when we're rendering near 1x; at >=1.5x the extra samples cost far more than they show.
    const samples = this.pixelRatio <= 1.25 ? this.quality.msaa : 0;
    const scenePass = pass(this.blankScene, this.blankCamera, { samples });
    this.scenePass = scenePass;
    const tex = scenePass.getTextureNode('output');
    // Chromatic hit-pulse: split R/B along the screen-centre direction.
    const dir = screenUV.sub(0.5);
    const off = dir.mul(this.uChroma);
    const shifted = vec4(tex.sample(screenUV.add(off)).r, tex.sample(screenUV).g, tex.sample(screenUV.sub(off)).b, float(1));
    let color = shifted;
    if (this.quality.bloom) {
      const b = this.settings.bloom!;
      this.bloomNode = bloom(tex, b.intensity ?? 0.9, b.radius ?? 0.7, b.threshold ?? 0.62);
      color = color.add(this.bloomNode) as typeof color;
    }
    const d = dir.length();
    const vig = float(1).sub(smoothstep(this.uVigOffset, this.uVigOffset.add(0.55), d).mul(this.uVigDark));
    const out = vec4(color.rgb.mul(vig), float(1));
    const pipeline = new RenderPipeline(this.renderer);
    pipeline.outputNode = out;
    this.pipeline = pipeline;
    void vec2;
  }

  private applyToneMapping(): void {
    const s = this.settings;
    this.renderer.toneMapping = s.toneMapping === 'agx' ? AgXToneMapping : ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = s.exposure ?? 1;
  }

  /** Reset to defaults then apply stage settings. Called by Game before each stage load. */
  reset(): void {
    this.settings = structuredClone(DEFAULTS);
    this.configure({});
  }

  configure(s: PostFxSettings): void {
    this.settings = {
      ...this.settings,
      ...s,
      bloom: { ...this.settings.bloom, ...s.bloom },
      vignette: { ...this.settings.vignette, ...s.vignette },
    };
    if (this.bloomNode) {
      const b = this.settings.bloom!;
      this.bloomNode.strength.value = this.bloomDisabled ? 0 : b.intensity ?? 1;
      this.bloomNode.threshold.value = b.threshold ?? 0.6;
      this.bloomNode.radius.value = b.radius ?? 0.7;
    }
    this.uVigOffset.value = this.settings.vignette!.offset ?? 0.3;
    this.uVigDark.value = this.settings.vignette!.darkness ?? 0.5;
    this.applyToneMapping();
  }

  pulse(strength = 1): void {
    this.pulseAmount = Math.min(2, this.pulseAmount + strength);
  }

  setView(scene: Scene, camera: Camera): void {
    this.scene = scene;
    this.camera = camera;
    if (this.scenePass) {
      this.scenePass.scene = scene;
      this.scenePass.camera = camera;
    }
  }

  resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(w, h, false);
  }

  get size(): { width: number; height: number } {
    return { width: this.canvas.clientWidth || window.innerWidth, height: this.canvas.clientHeight || window.innerHeight };
  }

  render(frameDt: number): void {
    if (!this.ready || !this.scene || !this.camera) return;
    this.trackPerformance(frameDt);
    this.pulseAmount = Math.max(0, this.pulseAmount - frameDt * 2.5);
    this.uChroma.value = (this.settings.chromatic ?? 0) * 2 + this.pulseAmount * 0.012;
    if (this.pipeline) this.pipeline.render();
    else this.renderer.render(this.scene, this.camera);
  }

  /**
   * Performance governor: first trade resolution (down to the tier's minPixelRatio), then bloom,
   * then remember a lower tier for the next load. Raises resolution back when there's headroom.
   */
  private slowStrikes = 0;
  private bloomDisabled = false;
  private capped = false;
  private trackPerformance(frameDt: number): void {
    this.frameTimes.push(frameDt);
    if (this.frameTimes.length > 90) this.frameTimes.shift();
    const now = performance.now();
    if (now - this.lastAdjust < 1000 || this.frameTimes.length < 30) return;
    if (document.hidden) return;
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    const fps = 1 / Math.max(1e-4, avg);
    const maxPr = this.maxPixelRatio();
    const minPr = Math.min(maxPr, this.quality.minPixelRatio);
    let next = this.pixelRatio;
    if (fps < 45) {
      // big steps when far below target so a heavy scene recovers within a couple of seconds
      if (this.pixelRatio > minPr + 0.01) next = Math.max(minPr, this.pixelRatio - (fps < 30 ? 0.35 : 0.2));
      else if (++this.slowStrikes >= 3) {
        this.slowStrikes = 0;
        if (this.bloomNode && !this.bloomDisabled) {
          this.bloomDisabled = true;
          this.bloomNode.strength.value = 0;
        } else if (this.quality.tier !== 'low' && !this.capped) {
          this.capped = true;
          capAutoTier(this.quality.tier === 'high' ? 'medium' : 'low');
        }
      }
    } else if (fps > 57 && this.pixelRatio < maxPr) {
      next = Math.min(maxPr, this.pixelRatio + 0.1);
      this.slowStrikes = 0;
    } else this.slowStrikes = 0;
    if (next !== this.pixelRatio) {
      this.pixelRatio = next;
      this.resize();
      this.frameTimes.length = 0;
    }
    this.lastAdjust = now;
  }

  /** Tier max pixel ratio, further capped so the canvas never exceeds the tier's pixel budget. */
  private maxPixelRatio(): number {
    const dpr = window.devicePixelRatio || 1;
    const { width, height } = this.size;
    const css = Math.max(1, width * height);
    const budget = Math.sqrt((PIXEL_BUDGET[this.quality.tier] ?? 3.7e6) / css);
    return Math.max(0.5, Math.min(dpr, this.quality.maxPixelRatio, budget));
  }

  get fps(): number {
    if (!this.frameTimes.length) return 0;
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    return 1 / Math.max(1e-4, avg);
  }

  /** Capture the current frame as a small data URL (for transitions). */
  snapshot(maxWidth = 640): string | undefined {
    try {
      if (!this.ready || !this.scene || !this.camera) return undefined;
      if (this.pipeline) this.pipeline.render();
      else this.renderer.render(this.scene, this.camera);
      const src = this.canvas;
      const scale = Math.min(1, maxWidth / src.width);
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(src.width * scale));
      c.height = Math.max(1, Math.round(src.height * scale));
      c.getContext('2d')!.drawImage(src, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.8);
    } catch {
      return undefined;
    }
  }
}
